import { goToStep, loadAuthState } from "../auth";
import { type Captcha, isCaptchaError, mountCaptcha } from "../captcha";
import { nextStep } from "../routes";
import { checkPassword } from "../password";
import { resendWaitSeconds } from "../resend";
import { supabase } from "../supabase";
import { t } from "../strings/en";
import { applyStrings, byId, setBusy, setMessage, showPanel } from "../ui";

applyStrings();

const form = byId<HTMLFormElement>("form");
const orgName = byId<HTMLInputElement>("orgName");
const email = byId<HTMLInputElement>("email");
const password = byId<HTMLInputElement>("password");
const submit = byId<HTMLButtonElement>("submit");
const message = byId("message");
const checkEmailBody = byId("checkEmailBody");
const checkEmailActions = byId("checkEmailActions");
const resend = byId<HTMLButtonElement>("resend");
const resendMessage = byId("resendMessage");
const rules = ["length", "upper", "lower", "digit", "symbol"] as const;
const captcha = mountCaptcha(byId("captcha"));
// O do reenvio só nasce quando o painel aparece (Turnstile não desenha em painel escondido).
let resendCaptcha: Captcha | null = null;

function refreshRules(): boolean {
  const checks = checkPassword(password.value);
  for (const r of rules) byId(`rule-${r}`).dataset.met = String(checks[r]);
  submit.disabled = !checks.ok;
  return checks.ok;
}

// Reenviar: o endereço do cadastro desta aba e quando o último e-mail saiu.
let sentTo: string | null = null;
let sentAt = 0;
let timer: number | undefined;

function refreshResend(): void {
  const wait = resendWaitSeconds(sentAt, Date.now());
  resend.disabled = wait > 0;
  resend.textContent = wait > 0 ? t("checkEmailResendIn", { seconds: wait }) : t("checkEmailResend");
  if (wait === 0 && timer !== undefined) {
    clearInterval(timer);
    timer = undefined;
  }
}

function startCooldown(): void {
  sentAt = Date.now();
  timer ??= window.setInterval(refreshResend, 1000);
  refreshResend();
}

// check-email é um estado desta página: o texto mostra o endereço quando o conhecemos.
// Sem o endereço (veio do login pelo hash), não há o que reenviar nem trocar.
function showCheckEmail(address: string | null): void {
  checkEmailBody.textContent = address ? t("checkEmailBody", { email: address }) : t("checkEmailBodyNoAddress");
  checkEmailActions.hidden = address === null;
  showPanel("panel-check-email");
  if (address !== null) {
    sentTo = address;
    startCooldown();
    resendCaptcha ??= mountCaptcha(byId("resendCaptcha"));
  }
}

resend.addEventListener("click", async () => {
  if (sentTo === null) return;
  setMessage(resendMessage, null);
  const blocked = resendCaptcha?.blocker() ?? null;
  if (blocked) return setMessage(resendMessage, blocked);
  resend.disabled = true;
  try {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: sentTo,
      options: { emailRedirectTo: `${location.origin}/login.html`, captchaToken: resendCaptcha?.token() },
    });
    if (isCaptchaError(error)) {
      setMessage(resendMessage, t("captchaFailed"));
      refreshResend();
      return;
    }
    setMessage(resendMessage, error ? t("checkEmailResendFailed") : t("checkEmailResent"));
    // Com erro também espera: quase sempre é o limite de envio do Auth.
    startCooldown();
  } catch {
    setMessage(resendMessage, t("networkError"));
    refreshResend();
  } finally {
    resendCaptcha?.reset();
  }
});

async function init(): Promise<void> {
  const step = nextStep(await loadAuthState());
  if (step !== "login" && step !== "check-email") return goToStep(step);
  if (location.hash === "#check-email") showCheckEmail(null);
  else showPanel("panel-form");
}

password.addEventListener("input", refreshRules);
refreshRules();

form.addEventListener("submit", async (e) => {
  e.preventDefault();
  setMessage(message, null);
  if (!refreshRules()) return;
  if (orgName.value.trim() === "") return setMessage(message, t("signupOrgRequired"));
  const blocked = captcha.blocker();
  if (blocked) return setMessage(message, blocked);
  setBusy(submit, true);
  try {
    const { error } = await supabase.auth.signUp({
      email: email.value.trim(),
      password: password.value,
      options: {
        data: { org_name: orgName.value.trim() },
        emailRedirectTo: `${location.origin}/login.html`,
        captchaToken: captcha.token(),
      },
    });
    if (error) {
      if (isCaptchaError(error)) return setMessage(message, t("captchaFailed"));
      setMessage(message, error.code === "user_already_exists" ? t("signupExists") : t(error.status === 429 ? "authTooMany" : "genericError"));
      return;
    }
    showCheckEmail(email.value.trim());
  } catch {
    setMessage(message, t("networkError"));
  } finally {
    captcha.reset();
    setBusy(submit, false);
    refreshRules();
  }
});

void init();
